import axios from 'axios';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { GridSelectionModel } from '@mui/x-data-grid/models/gridSelectionModel';
import { TabIndexEnum } from '@modules/ContactManager/components/Header/interfaces';
import { formatDate, queryParams, withQueryParams } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { dateFormatWithTime } from '@shared/constants/formats';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { useTabs } from '@shared/hooks/useTabs';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectViewAsUserId, dealQueueCount } from '@store/reducers/headerSlice';
import { ViewHeightWrapper } from '@styles/CommonStyles';
import { useServerSide } from '@shared/hooks/useServerSide';

import { IDeals, IDealsQueueQueryParams, IDealsResponse, ILcQueueProps } from '../types';

const columns: GridColDef[] = [
  dataGridCol('dealNumericId'),
  dataGridCol('contactDate', {
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
  dataGridCol('leadSource'),
  dataGridCol('firstName'),
  dataGridCol('lastName'),
  dataGridCol('dealUsers'),
  dataGridCol('state'),
  dataGridCol('timeZone'),
  dataGridCol('loanAmount', { ...gridCurrencyCell }),
  dataGridCol('assetTag'),
  dataGridCol('activeDealStatus'),
  dataGridCol('statusDate', {
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
  dataGridCol('lastCommentDate', {
    valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    type: 'dateTime',
  }),
  dataGridCol('bankName', {
    headerName: 'Bank',
  }),
].map((column) => ({ flex: 1, ...column }));

const DealsPage = ({ title }: ILcQueueProps) => {
  const params = queryParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openTab } = useTabs();
  const viewAs = useAppSelector(selectViewAsUserId);

  const pageSize = 50;
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const { requestPayload, onSortModelChange, onFilterModelChange, handleClosePanel, onPageChange } = useServerSide();
  const [items, setItems] = useState<IDeals[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const {
    data: dealsResponse,
    mutate,
    isValidating,
    error,
  } = useSWR<IDealsResponse>(
    [
      withQueryParams(`/${SERVICES.SEARCH}/search/filtered/dealqueuebyid`, {
        queueId: params.queueId,
        viewAsUserId: viewAs?.userId,
      }),
      { ...requestPayload, pageSize },
    ],
    ([url, payload]) => axios.post(url, payload),
    { revalidateIfStale: true }
  );

  useEffect(() => {
    mutate();
  }, [requestPayload]);

  useEffect(() => {
    if (dealsResponse) {
      setItems(dealsResponse?.data?.items);
      setTotalCount(dealsResponse?.data?.totalCount);
      dispatch(dealQueueCount(dealsResponse?.data?.totalCount));
    }
  }, [dealsResponse]);

  const rowClick = useCallback(
    (row: IDeals) => {
      const pageNumber = requestPayload?.pageNumber ?? 1;
      const offSetValue = pageSize * (pageNumber - 1);

      const recordIndex = items?.findIndex((item) => item.dealId === row.dealId);
      const recordNumber = recordIndex >= 0 ? offSetValue + recordIndex + 1 : 0;
      const queryParams: IDealsQueueQueryParams = {
        id: row.contactId,
        dealId: row.dealId,
        tab: TabIndexEnum.Deals,
        queueId: params.queueId,
        recordNumber,
        isDealsQueue: true,
      };
      
      if (requestPayload?.orderBy) {
        queryParams.orderBy = requestPayload.orderBy;
      }
      
      if (requestPayload?.orderByDirection != null) {
        queryParams.orderByDirection = requestPayload.orderByDirection;
      }      
      const route = withQueryParams('/contact-manager', queryParams, false);
      const title = `${row.firstName} ${row.lastName}`;

      openTab({ title, route, shouldCloseDuplicate: true });
      navigate(route);
    },
    [items, params.queueId, requestPayload?.pageNumber, pageSize]
  );

  const onSelectionModelChange = useCallback((model: GridSelectionModel) => {
    model && setSelectionModel(model);
  }, []);

  return (
    <ViewHeightWrapper>
      <PhoenixGrid
        withHeader
        noButton
        strict
        disableSelectionOnClick
        showClearAllFilter
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        name={title}
        columns={columns}
        loading={(!items && !error) || isValidating}
        rows={items && !isValidating ? items : []}
        selectionModel={selectionModel}
        pageSize={pageSize}
        rowCount={totalCount}
        getRowId={(row) => row.dealId}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        onPreferencePanelClose={handleClosePanel}
        onSelectionModelChange={onSelectionModelChange}
        onPageChange={onPageChange}
        onRowClick={({ row }: { row: IDeals }) => rowClick(row)}
      />
    </ViewHeightWrapper>
  );
};

export const Deals = withOpenTab(DealsPage);
